import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import getConfig from 'next/config';

import { setSentryConsent } from '../helpers/sentryConsent';

/**
 * Bridges the Didomi consent decisions into Sentry. Two separate vendors gate two features:
 *
 * - Error capture (`allowSentry`): Sentry's `beforeSend` (see `sentry.client.config.ts`) drops
 *   every event until this reports consent.
 * - Session Replay (`allowSentryReplay`): the replay integration is absent at init (see
 *   `sentry.client.config.ts`), so nothing records before consent. We lazy-add it here once consent
 *   is granted — `replaysOnErrorSampleRate: 1.0` then auto-starts buffer mode and uploads on the
 *   next error — and tear it down with `stop()` if consent is withdrawn. Replay is additionally
 *   gated by its own feature flag (`FEATURE_SENTRY_REPLAY`), independent of error tracking: when
 *   the flag is off the integration is never added, so replay never records regardless of consent.
 *
 * @param allowSentry Consent for the Sentry error-tracking vendor.
 * @param allowSentryReplay Consent for the separate Sentry Session Replay vendor.
 */
const useSentry = (allowSentry: boolean, allowSentryReplay: boolean): void => {
  useEffect(() => {
    setSentryConsent(allowSentry);
  }, [allowSentry]);

  useEffect(() => {
    const { FEATURE_SENTRY_REPLAY } = getConfig().publicRuntimeConfig ?? {};
    // Replay runs only when its feature flag is on AND its Didomi vendor is consented.
    // Flag off → treat exactly like no consent: never add/record, tear down if present.
    const replayEnabled = Boolean(FEATURE_SENTRY_REPLAY) && allowSentryReplay;

    if (!replayEnabled) {
      // stop() is async; we don't await teardown, so swallow any rejection.
      // No-op when the SDK isn't initialised (flag/DSN unset) or replay was never added.
      Sentry.getReplay()
        ?.stop()
        .catch(() => undefined);
      return;
    }

    const replay = Sentry.getReplay();
    if (replay) {
      // Re-arm after a prior withdrawal in the same page load.
      replay.startBuffering();
    } else {
      // First grant: privacy-first masking — redacted DOM reconstruction, never the raw page.
      Sentry.addIntegration(
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      );
    }
  }, [allowSentryReplay]);
};

export { useSentry };
